/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// allow-any-unicode-comment-file
// DO NOT EDIT -- auto-generated by scripts/sync-agent-host-protocol.ts

// Generated from types/actions.ts — do not edit
// Run `npm run generate` to regenerate.

import { ActionType, type IStateAction, type IRootAgentsChangedAction, type IRootActiveSessionsChangedAction, type ISessionReadyAction, type ISessionCreationFailedAction, type ISessionTurnStartedAction, type ISessionDeltaAction, type ISessionResponsePartAction, type ISessionToolCallStartAction, type ISessionToolCallDeltaAction, type ISessionToolCallReadyAction, type ISessionToolCallConfirmedAction, type ISessionToolCallCompleteAction, type ISessionToolCallResultConfirmedAction, type ISessionTurnCompleteAction, type ISessionTurnCancelledAction, type ISessionErrorAction, type ISessionTitleChangedAction, type ISessionUsageAction, type ISessionReasoningAction, type ISessionModelChangedAction, type ISessionServerToolsChangedAction, type ISessionActiveClientChangedAction, type ISessionActiveClientToolsChangedAction, type ISessionPendingMessageSetAction, type ISessionPendingMessageRemovedAction, type ISessionQueuedMessagesReorderedAction, type ISessionCustomizationsChangedAction, type ISessionCustomizationToggledAction, type ISessionTruncatedAction } from './actions.js';


// ─── Root vs Session Action Unions ───────────────────────────────────────────

/** Union of all root-scoped actions. */
export type IRootAction =
	| IRootAgentsChangedAction
	| IRootActiveSessionsChangedAction
	;

/** Union of all session-scoped actions. */
export type ISessionAction =
	| ISessionReadyAction
	| ISessionCreationFailedAction
	| ISessionTurnStartedAction
	| ISessionDeltaAction
	| ISessionResponsePartAction
	| ISessionToolCallStartAction
	| ISessionToolCallDeltaAction
	| ISessionToolCallReadyAction
	| ISessionToolCallConfirmedAction
	| ISessionToolCallCompleteAction
	| ISessionToolCallResultConfirmedAction
	| ISessionTurnCompleteAction
	| ISessionTurnCancelledAction
	| ISessionErrorAction
	| ISessionTitleChangedAction
	| ISessionUsageAction
	| ISessionReasoningAction
	| ISessionModelChangedAction
	| ISessionServerToolsChangedAction
	| ISessionActiveClientChangedAction
	| ISessionActiveClientToolsChangedAction
	| ISessionPendingMessageSetAction
	| ISessionPendingMessageRemovedAction
	| ISessionQueuedMessagesReorderedAction
	| ISessionCustomizationsChangedAction
	| ISessionCustomizationToggledAction
	| ISessionTruncatedAction
	;

/** Union of session actions that clients may dispatch. */
export type IClientSessionAction =
	| ISessionTurnStartedAction
	| ISessionToolCallConfirmedAction
	| ISessionToolCallCompleteAction
	| ISessionToolCallResultConfirmedAction
	| ISessionTurnCancelledAction
	| ISessionTitleChangedAction
	| ISessionModelChangedAction
	| ISessionActiveClientChangedAction
	| ISessionActiveClientToolsChangedAction
	| ISessionPendingMessageSetAction
	| ISessionPendingMessageRemovedAction
	| ISessionQueuedMessagesReorderedAction
	| ISessionCustomizationToggledAction
	| ISessionTruncatedAction
	;

/** Union of session actions that only the server may produce. */
export type IServerSessionAction =
	| ISessionReadyAction
	| ISessionCreationFailedAction
	| ISessionDeltaAction
	| ISessionResponsePartAction
	| ISessionToolCallStartAction
	| ISessionToolCallDeltaAction
	| ISessionToolCallReadyAction
	| ISessionTurnCompleteAction
	| ISessionErrorAction
	| ISessionUsageAction
	| ISessionReasoningAction
	| ISessionServerToolsChangedAction
	| ISessionCustomizationsChangedAction
	;

// ─── Client-Dispatchable Map ─────────────────────────────────────────────────

/**
 * Exhaustive map indicating which action types may be dispatched by clients.
 * Adding a new action to IStateAction without adding it here is a compile error.
 */
export const IS_CLIENT_DISPATCHABLE: { readonly [K in IStateAction['type']]: boolean } = {
	[ActionType.RootAgentsChanged]: false,
	[ActionType.RootActiveSessionsChanged]: false,
	[ActionType.SessionReady]: false,
	[ActionType.SessionCreationFailed]: false,
	[ActionType.SessionTurnStarted]: true,
	[ActionType.SessionDelta]: false,
	[ActionType.SessionResponsePart]: false,
	[ActionType.SessionToolCallStart]: false,
	[ActionType.SessionToolCallDelta]: false,
	[ActionType.SessionToolCallReady]: false,
	[ActionType.SessionToolCallConfirmed]: true,
	[ActionType.SessionToolCallComplete]: true,
	[ActionType.SessionToolCallResultConfirmed]: true,
	[ActionType.SessionTurnComplete]: false,
	[ActionType.SessionTurnCancelled]: true,
	[ActionType.SessionError]: false,
	[ActionType.SessionTitleChanged]: true,
	[ActionType.SessionUsage]: false,
	[ActionType.SessionReasoning]: false,
	[ActionType.SessionModelChanged]: true,
	[ActionType.SessionServerToolsChanged]: false,
	[ActionType.SessionActiveClientChanged]: true,
	[ActionType.SessionActiveClientToolsChanged]: true,
	[ActionType.SessionPendingMessageSet]: true,
	[ActionType.SessionPendingMessageRemoved]: true,
	[ActionType.SessionQueuedMessagesReordered]: true,
	[ActionType.SessionCustomizationsChanged]: false,
	[ActionType.SessionCustomizationToggled]: true,
	[ActionType.SessionTruncated]: true,
};
